import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { toast } from 'sonner';
import { SubscriptionPlan, PartnerSubscriptionPlan, CreatePlanData } from '@/types/partners';


export function usePartnerPlans(partnerType: 'co_branded' | 'white_label') {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['partner-plans', partnerType, user?.id],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const { data, error } = await supabase.rpc('get_partner_plans', {
        partner_user_type: partnerType,
        requesting_partner_id: user?.id
      });

      if (error) {
        throw new Error(`Failed to fetch partner plans: ${error.message}`);
      }

      return data || [];
    },
    enabled: !!partnerType
  });
}

// Hook to get partner plans (custom plans)
export function usePlans() {
  return useQuery<PartnerSubscriptionPlan[]>({
    queryKey: ['partner-plans'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/subscription-plans`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription plans');
      }

      return response.json();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to create a new subscription plan
export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation<PartnerSubscriptionPlan, Error, CreatePlanData>({
    mutationFn: async (planData) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/subscription-plans`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(planData),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create subscription plan');
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Invalidate and refetch all plan-related queries
      queryClient.invalidateQueries({ queryKey: ['partner-subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['partner-plans'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      
      // Also invalidate subscription links
      queryClient.invalidateQueries({ queryKey: ['partner-subscription-links'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-links'] });
      
      // Invalidate any pricing-related queries
      queryClient.invalidateQueries({ queryKey: ['partner-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-tiers'] });
      
      toast.success(`Subscription plan "${data.name}" created successfully`);
    },
    onError: (error) => {
      console.error('Create plan error:', error);
      
      // Extract specific error message
      let errorMessage = 'Unknown error occurred';
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`Failed to create plan: ${errorMessage}`);
    },
  });
}

// Hook to update a subscription plan
export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation<PartnerSubscriptionPlan, Error, { id: string; data: Partial<PartnerSubscriptionPlan> }>({
    mutationFn: async ({ id, data }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/subscription-plans/${id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.error || 'Failed to update subscription plan');
        // Add field info to error for better handling in components
        (error as any).field = errorData.field;
        throw error;
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch all plan-related queries
      queryClient.invalidateQueries({ queryKey: ['partner-subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['partner-plans'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      
      // Also invalidate subscription links
      queryClient.invalidateQueries({ queryKey: ['partner-subscription-links'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-links'] });
    },
  });
}

// Hook to delete a subscription plan
export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (planId) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/subscription-plans/${planId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete subscription plan');
      }
    },
    onSuccess: () => {
      // Invalidate and refetch all plan-related queries
      queryClient.invalidateQueries({ queryKey: ['partner-subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['partner-plans'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      
      // Also invalidate subscription links
      queryClient.invalidateQueries({ queryKey: ['partner-subscription-links'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-links'] });
      
      toast.success('Subscription plan deleted successfully');
    },
    onError: (error) => {
      console.error('Delete plan error:', error);
      // Error handling is done in the component, no toast here to avoid duplication
    },
  });
}